"use client";
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useCart } from '../../context/CartContext';
import { useGateway } from '../../hooks/useGateway';
import { useSelectedCurrency } from '../../hooks';
import { useErrorMessage } from '../../hooks/useErrorMessage';
import { useProductUpdates } from '../../hooks/useProductUpdates';
import { Card, CardVariant, PricingResult, isCardOutOfStock, isVariantOutOfStock, isStockSufficient, CardLocale, getCardImage, getCardTitle } from '../../types/card';
import { useTranslations, useLocale } from 'next-intl';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import {
  findMatchingVariant,
  validateValue,
  lookupPrice,
  convertAmount,
  pickDefaultCustomValue,
} from './productUtils';
import { DEFAULT_PDP_AMOUNT } from '@/constants';
import { trackViewContent, trackAddToCart } from '../MetaPixel/MetaPixel';
import { GaEcommerceEvent, GaEcommerceItem, trackGaEcommerce } from '../GoogleAnalytics/GoogleAnalytics';
import { VariantSelector } from './VariantSelector';
import { PriceDisplay } from './PriceDisplay';
import { QuantitySelector } from './QuantitySelector';
import { ActionButtons } from './ActionButtons';
import styles from './StyledComponents.module.css';

const AddedToCartModal = dynamic(() => import('./AddedToCartModal'), { ssr: false });
const ProductUpdatesModal = dynamic(() => import('./ProductUpdatesModal').then(m => ({ default: m.ProductUpdatesModal })), { ssr: false });

interface PreComputedDisplay {
  price: number;
  priceNoDiscount: number;
  savings: number;
  faceValue: number;
  faceValueCurrency: string;
  fee: number;
  currency: string;
  isConverted: boolean;
}

interface ProductInteractiveProps {
  product: Card;
  initialPrice: PricingResult | undefined;
  initialVariantIndex: number;
  initialCustomValue: number | null;
  pricingMap: Record<string, PricingResult>;
  rateMap: Record<string, number>;
  preComputedDisplayPrices: Record<string, PreComputedDisplay>;
}

export const ProductInteractive: React.FC<ProductInteractiveProps> = ({ product, initialPrice, initialVariantIndex, initialCustomValue, pricingMap, rateMap, preComputedDisplayPrices }) => {
  const t = useTranslations('product');
  const tCart = useTranslations('cart');
  const locale = useLocale() as CardLocale;
  const { localizeFullPath } = useLocalizedPath();
  const router = useRouter();
  const { gateway, handleRequest } = useGateway();
  const { addToCart, cart } = useCart();
  const { selectedCurrency } = useSelectedCurrency();
  const resolveErrorMessage = useErrorMessage();

  const [selectedVariantIndex, setSelectedVariantIndex] = useState(initialVariantIndex);
  const [customValue, setCustomValue] = useState<number | null>(initialCustomValue);
  const [quantity, setQuantity] = useState(1);
  const [validationMsg, setValidationMsg] = useState<string | null>(null);
  const [addedToCartModalOpen, setAddedToCartModalOpen] = useState(false);
  const [addedProductInfo, setAddedProductInfo] = useState<{
    title: string; logo?: string; value: number; currency: string; quantity: number;
  } | null>(null);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const productUpdates = useProductUpdates(product._id);
  const [cartError, setCartError] = useState<string | null>(null);
  const [fetchedPrice, setFetchedPrice] = useState<PricingResult | undefined>(undefined);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const selectedVariant = product.variants?.[selectedVariantIndex];
  const selectedValue = customValue ?? selectedVariant?.faceValue.max ?? null;
  const cardOutOfStock = isCardOutOfStock(product);
  const variantOutOfStock = selectedVariant ? isVariantOutOfStock(selectedVariant) : false;
  const outOfStock = cardOutOfStock || variantOutOfStock;
  // The updates modal starts on this amount only while the card still has
  // others; a sold-out card is followed as a whole. The customer can flip it.
  const wantedVariant = !cardOutOfStock && variantOutOfStock ? selectedVariant : undefined;

  const price = useMemo(() => {
    if (!selectedValue || !selectedVariant) return undefined;
    const country = selectedVariant.countries?.[0];
    const cached = lookupPrice(pricingMap, product._id, selectedValue, selectedVariant.currency, country);
    if (cached) return cached;
    if (fetchedPrice) return fetchedPrice;
    if (selectedVariantIndex === initialVariantIndex && customValue === initialCustomValue && initialPrice) return initialPrice;
    return undefined;
  }, [selectedValue, selectedVariant, pricingMap, product._id, fetchedPrice, selectedVariantIndex, customValue, initialPrice, initialVariantIndex, initialCustomValue]);

  const needsClientFetch = useMemo(() => {
    if (!selectedValue || !selectedVariant) return false;
    const country = selectedVariant.countries?.[0];
    const cached = lookupPrice(pricingMap, product._id, selectedValue, selectedVariant.currency, country);
    return !cached && !(selectedVariantIndex === initialVariantIndex && customValue === initialCustomValue && initialPrice);
  }, [selectedValue, selectedVariant, pricingMap, product._id, selectedVariantIndex, customValue, initialPrice, initialVariantIndex, initialCustomValue]);

  const savings = useMemo(() => {
    if (!price || !selectedValue) return 0;
    return selectedValue - price.price > 0 ? selectedValue - price.price : 0;
  }, [price, selectedValue]);

  const cartQuantityForVariant = useMemo(() => {
    if (!cart?.products || !selectedVariant) return 0;
    return cart.products
      .filter(item =>
        item.productId === product._id &&
        item.currency === selectedVariant.currency &&
        item.selectedValue >= selectedVariant.faceValue.min &&
        item.selectedValue <= selectedVariant.faceValue.max
      )
      .reduce((sum, item) => sum + item.quantity, 0);
  }, [cart?.products, product._id, selectedVariant]);

  const canAddToCart = useMemo(() => {
    if (!selectedValue || !selectedVariant) return false;
    return isStockSufficient(product, selectedValue, selectedVariant.currency, cartQuantityForVariant + quantity, selectedVariant.countries?.[0]);
  }, [product, selectedValue, selectedVariant, cartQuantityForVariant, quantity]);

  const priceDisplay = useMemo(() => {
    if (!price || !selectedVariant || !selectedValue) return null;
    // Use the server-precomputed entry whenever the user is on the default
    // selection for this variant (fixed variant with no customValue, or
    // range variant with customValue still equal to the prefetched default).
    // The precomputed table is built per (variant, display currency) so the
    // user's globally-selected currency is honored on initial render.
    const isDefaultSelection =
      selectedVariantIndex === initialVariantIndex && customValue === initialCustomValue;
    if (customValue === null || isDefaultSelection) {
      const preComputed = preComputedDisplayPrices[`${selectedVariantIndex}:${selectedCurrency}`];
      if (preComputed) return preComputed;
    }
    const conv = convertAmount(price.price, selectedVariant.currency, selectedCurrency, rateMap);
    const noDiscConv = convertAmount(price.price_no_discount, selectedVariant.currency, selectedCurrency, rateMap);
    const savingsConv = convertAmount(savings, selectedVariant.currency, selectedCurrency, rateMap);
    const variantFee = Math.max(0, price.price - selectedValue);
    const feeConv = convertAmount(variantFee, selectedVariant.currency, selectedCurrency, rateMap);
    return {
      price: conv.converted,
      priceNoDiscount: noDiscConv.converted,
      savings: savingsConv.converted,
      faceValue: selectedValue,
      faceValueCurrency: selectedVariant.currency,
      fee: feeConv.converted,
      currency: conv.currency,
      isConverted: conv.isConverted,
    };
  }, [price, selectedVariant, selectedValue, selectedCurrency, rateMap, savings, customValue, selectedVariantIndex, preComputedDisplayPrices, initialVariantIndex, initialCustomValue]);

  const fetchPriceForValue = useCallback(async (value: number, variant: CardVariant) => {
    const validation = validateValue(value, variant, t);
    if (validation) {
      setValidationMsg(validation);
      setFetchedPrice(undefined);
      setLoadingPrice(false);
      return;
    }
    const cached = lookupPrice(pricingMap, product._id, value, variant.currency, variant.countries?.[0]);
    if (cached) {
      setFetchedPrice(cached);
      setLoadingPrice(false);
      return;
    }
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    setLoadingPrice(true);
    setFetchedPrice(undefined);
    try {
      const result = await handleRequest(() =>
        gateway.pricing.getPricingFromCardId(product._id, value, variant.currency, variant.countries?.[0])
      );
      if (!controller.signal.aborted && result) setFetchedPrice(result);
    } catch {
      if (!controller.signal.aborted) setFetchedPrice(undefined);
    } finally {
      if (!controller.signal.aborted) setLoadingPrice(false);
    }
  }, [pricingMap, product._id, gateway.pricing, handleRequest, t]);

  const handleVariantSelect = useCallback((index: number) => {
    const newVariant = product.variants?.[index];
    if (!newVariant) return;
    setSelectedVariantIndex(index);
    setQuantity(1);
    setCustomValue(pickDefaultCustomValue(newVariant, DEFAULT_PDP_AMOUNT));
    setValidationMsg(null);
    setFetchedPrice(undefined);
    setLoadingPrice(false);
  }, [product.variants]);

  const handleCustomValueChange = useCallback((value: number | null, variant: CardVariant) => {
    setCustomValue(value);
    if (value === null) {
      setValidationMsg(null);
      setFetchedPrice(undefined);
      return;
    }
    const currentVariant = product.variants?.[selectedVariantIndex];
    const inCurrentRange = currentVariant && value >= currentVariant.faceValue.min && value <= currentVariant.faceValue.max;
    if (!inCurrentRange) {
      const matching = findMatchingVariant(product.variants || [], value);
      if (matching) {
        const newIndex = product.variants?.indexOf(matching) ?? 0;
        if (newIndex !== selectedVariantIndex) {
          setSelectedVariantIndex(newIndex);
          setQuantity(1);
        }
      }
    }
    const validation = validateValue(value, variant, t);
    setValidationMsg(validation);
    if (!validation) fetchPriceForValue(value, variant);
    else { setFetchedPrice(undefined); setLoadingPrice(false); }
  }, [product.variants, selectedVariantIndex, t, fetchPriceForValue]);

  const handleCustomValueBlur = useCallback((value: string, variant: CardVariant) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed) || value === '') { setCustomValue(null); setValidationMsg(null); return; }
    handleCustomValueChange(parsed, variant);
  }, [handleCustomValueChange]);

  const handleQuantityChange = useCallback((value: number) => {
    if (value > 0) setQuantity(value);
  }, []);

  // Canonical name, not the localized title: GA4 item reports then don't
  // split by locale and join the backend purchase rows (same item_id/name).
  const gaItem = useCallback((qty: number): GaEcommerceItem => ({
    item_id: product._id,
    item_name: product.name,
    item_variant: `${selectedValue} ${selectedVariant?.currency}`,
    price: price?.price,
    quantity: qty,
  }), [product, selectedValue, selectedVariant, price]);

  const handleAddToCart = useCallback(async () => {
    if (!selectedValue || !price || !selectedVariant) return;
    setCartError(null);
    try {
      const metaEventId = trackAddToCart(product._id, getCardTitle(product, locale), price.price, selectedVariant.currency, quantity) ?? undefined;
      await addToCart(product._id, quantity, selectedValue, selectedVariant.currency, selectedVariant.countries?.[0], metaEventId);
      trackGaEcommerce(GaEcommerceEvent.AddToCart, selectedVariant.currency, price.price * quantity, [gaItem(quantity)]);
      setAddedProductInfo({ title: getCardTitle(product, locale), logo: getCardImage(product), value: selectedValue, currency: selectedVariant.currency, quantity });
      setAddedToCartModalOpen(true);
    } catch (error: any) {
      setCartError(resolveErrorMessage(error));
    }
  }, [selectedValue, price, selectedVariant, product, quantity, addToCart, locale, resolveErrorMessage, gaItem]);

  const handleBuyNow = useCallback(async () => {
    if (!selectedValue || !price || !selectedVariant) return;
    setCartError(null);
    try {
      const metaEventId = trackAddToCart(product._id, getCardTitle(product, locale), price.price, selectedVariant.currency, quantity) ?? undefined;
      await addToCart(product._id, quantity, selectedValue, selectedVariant.currency, selectedVariant.countries?.[0], metaEventId);
      trackGaEcommerce(GaEcommerceEvent.AddToCart, selectedVariant.currency, price.price * quantity, [gaItem(quantity)]);
      router.push(localizeFullPath('/cart') + '?checkout=true');
    } catch (error: any) {
      setCartError(resolveErrorMessage(error));
    }
  }, [selectedValue, price, selectedVariant, product, quantity, addToCart, router, localizeFullPath, resolveErrorMessage, gaItem]);

  const initialFetchDone = useRef(false);
  useEffect(() => {
    if (needsClientFetch && !loadingPrice && !fetchedPrice && !initialFetchDone.current && selectedVariant && selectedValue) {
      initialFetchDone.current = true;
      fetchPriceForValue(selectedValue, selectedVariant);
    }
  }, [needsClientFetch, loadingPrice, fetchedPrice, selectedVariant, selectedValue, fetchPriceForValue]);

  // Fire Meta Pixel ViewContent once per product page view. Wait until we have
  // a price so the value passed to Meta is meaningful for ad optimization.
  const viewContentFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedVariant || !selectedValue || !price) return;
    const key = product._id;
    if (viewContentFiredRef.current === key) return;
    viewContentFiredRef.current = key;
    trackViewContent(product._id, getCardTitle(product, locale), price.price, selectedVariant.currency);
    trackGaEcommerce(GaEcommerceEvent.ViewItem, selectedVariant.currency, price.price, [gaItem(1)]);
  }, [product, selectedVariant, selectedValue, price, locale, gaItem]);

  // View-count beacon: the PDP read is edge-cached, so the increment no longer
  // rides along with the SSR fetch. Fire once on mount; bots that don't run JS
  // are excluded, which is the intent.
  const viewBeaconFiredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!product.slug || viewBeaconFiredRef.current === product.slug) return;
    viewBeaconFiredRef.current = product.slug;
    fetch('/api/view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'product', id: product.slug }),
      keepalive: true,
    }).catch(() => {});
  }, [product.slug]);

  return (
    // Read by CSS (:has) to flip the stock badge in the hydration-free image column.
    <div className={styles.productInfo} data-selected-stock={outOfStock ? 'out' : 'in'}>
      <div className={styles.pricingSection}>
        <VariantSelector
          product={product}
          selectedVariantIndex={selectedVariantIndex}
          customValue={customValue}
          validationMsg={validationMsg}
          outOfStock={outOfStock}
          onVariantSelect={handleVariantSelect}
          onCustomValueChange={handleCustomValueChange}
          onCustomValueBlur={handleCustomValueBlur}
        />

        <PriceDisplay
          loadingPrice={loadingPrice}
          price={price}
          selectedValue={selectedValue}
          validationMsg={validationMsg}
          savings={savings}
          priceDisplay={priceDisplay}
        />
      </div>

      <QuantitySelector
        product={product}
        selectedVariant={selectedVariant}
        selectedValue={selectedValue}
        quantity={quantity}
        cartQuantityForVariant={cartQuantityForVariant}
        outOfStock={outOfStock}
        onQuantityChange={handleQuantityChange}
      />

      <ActionButtons
        productId={product._id}
        outOfStock={outOfStock}
        loadingPrice={loadingPrice}
        hasPrice={!!price}
        canAddToCart={canAddToCart}
        cartError={cartError}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
        onNotify={() => setNotifyModalOpen(true)}
        updatesActive={productUpdates.following}
      />

      {notifyModalOpen && (
        <ProductUpdatesModal
          open={notifyModalOpen}
          onClose={() => setNotifyModalOpen(false)}
          product={product}
          selectedVariant={selectedVariant}
          preselectAmount={!!wantedVariant}
          rows={productUpdates.rows}
          onSubscribed={productUpdates.noteSubscribed}
          onStopped={productUpdates.noteStopped}
        />
      )}
      {addedToCartModalOpen && (
        <AddedToCartModal
          open={addedToCartModalOpen}
          onClose={() => setAddedToCartModalOpen(false)}
          addedProduct={addedProductInfo}
        />
      )}
    </div>
  );
};
